import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import * as z from 'zod';
import { readAuthToken } from '@noor/api';
import { env } from '@noor/config';
import {
  courierProfileQueries,
  courierTransactionsSearchSchema,
  courierOrdersSearchSchema,
  type CourierTransactionsSearch,
  type CourierOrdersSearch,
} from '@/entities/courier-profile';
import { COURIER_TABS, type CourierTab } from '@/widgets/courier-tabs';
import { CourierDetailPage, type CourierPeriod } from '@/pages/courier-detail';

/**
 * Courier Detail route — the `tab` search param is the single source of truth
 * for the active section. `beforeLoad` guards auth (only against a real API;
 * MSW authenticates everything), and the loader prefetches the profile so the
 * summary card hydrates from cache on navigation.
 */
const courierDetailSearchSchema = z.object({
  ...courierTransactionsSearchSchema.shape,
  ...courierOrdersSearchSchema.shape,
  tab: z.enum(COURIER_TABS).default('profile'),
});

export const Route = createFileRoute('/couriers/$courierId')({
  staticData: { permission: 'couriers.view' },
  beforeLoad: () => {
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
      // `redirect()` is TanStack Router's control-flow throw, not an Error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: courierDetailSearchSchema,
  loader: ({ context, params }) => {
    // Non-blocking prefetch — render the shell immediately; the page's hooks
    // surface loading/error.
    void context.queryClient
      .prefetchQuery(courierProfileQueries.detail(params.courierId))
      .catch(() => undefined);
  },
  component: CourierDetailRoute,
});

function CourierDetailRoute() {
  const { courierId } = Route.useParams();
  const {
    tab,
    orderStatus,
    orderPage,
    orderPageSize,
    orderFrom,
    orderTill,
    ...transactionsFilter
  } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const onTabChange = (next: CourierTab) => {
    void navigate({ search: (prev) => ({ ...prev, tab: next }) });
  };

  const onTransactionsChange = (patch: Partial<CourierTransactionsSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };
  const onOrdersChange = (patch: Partial<CourierOrdersSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };
  // The orders and transactions cards share one date window; write both
  // param pairs in a single navigation so neither update clobbers the other.
  const onPeriodChange = ({ from, till }: CourierPeriod) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        orderFrom: from,
        orderTill: till,
        orderPage: 1,
        txFrom: from,
        txTill: till,
        txPage: 1,
      }),
    });
  };
  return (
    <CourierDetailPage
      courierId={courierId}
      tab={tab}
      onTabChange={onTabChange}
      onOpenChat={(chat) => void navigate({ to: '/inbox', search: { chat } })}
      transactionsFilter={transactionsFilter}
      onTransactionsChange={onTransactionsChange}
      ordersFilter={{ orderStatus, orderPage, orderPageSize, orderFrom, orderTill }}
      onOrdersChange={onOrdersChange}
      onPeriodChange={onPeriodChange}
    />
  );
}
